import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Input } from "@/components/ui/Input/Input";
import { Textarea } from "@/components/ui/Textarea/Textarea";
import { AppShell } from "@/components/app/AppShell";
import { Field } from "@/components/auth/FormShell";
import { useAuth } from "@/lib/AuthContext";
import { useBrand, useCreateScanJob, useScanJob } from "@/lib/queries";
import styles from "./BrandLearn.module.css";

const MAX_URLS = 6;

/**
 * "Learn my brand" — point the scanner at the brand's existing public
 * presence (website / blog / pasted text). An async worker reads it,
 * extracts how the brand sounds, and auto-saves into the brain
 * (fill-only — it never wipes what you've set). On success we refresh
 * the brain and drop into the editor to review.
 */
export function BrandLearn() {
  const { ws, brand } = useParams<{ ws: string; brand: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const brandQuery = useBrand(ws, brand);
  const createScan = useCreateScanJob(ws, brand);
  const [jobId, setJobId] = useState<string | undefined>();
  const scanQuery = useScanJob(ws, brand, jobId);

  const [urls, setUrls] = useState<string[]>([""]);
  const [pastedText, setPastedText] = useState("");

  const job = scanQuery.data?.job;
  const running = job?.status === "queued" || job?.status === "running";

  // On a successful scan the brain was auto-saved — refresh it so the
  // editor shows the learned values.
  useEffect(() => {
    if (job?.status === "done") {
      void qc.invalidateQueries({ queryKey: ["brand", ws, brand] });
    }
  }, [job?.status, ws, brand, qc]);

  if (auth.status === "anonymous") {
    return <Navigate to={`/signin?next=/w/${ws}/b/${brand}/learn`} replace />;
  }

  const trimmedUrls = urls.map((u) => u.trim()).filter(Boolean);
  const canSubmit = !running && (trimmedUrls.length > 0 || !!pastedText.trim());

  const setUrlAt = (i: number, v: string) =>
    setUrls((prev) => prev.map((u, idx) => (idx === i ? v : u)));
  const addUrl = () => setUrls((prev) => (prev.length >= MAX_URLS ? prev : [...prev, ""]));
  const removeUrl = (i: number) =>
    setUrls((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)));

  async function onScan() {
    if (!canSubmit) return;
    const res = await createScan.mutateAsync({
      ...(trimmedUrls.length ? { urls: trimmedUrls } : {}),
      ...(pastedText.trim() ? { pastedText: pastedText.trim() } : {}),
    });
    setJobId(res.jobId);
  }

  return (
    <AppShell currentWorkspaceId={ws}>
      <div className={styles.page}>
        <Btn href={`/w/${ws}/b/${brand}`} variant="ghost" size="sm" iconLeft={<Icon name="arrow-left" size={13} />}>
          Back to brand
        </Btn>
        <header className={styles.head}>
          <p className={styles.kicker}>Learn</p>
          <h1 className={styles.title}>Teach the brain about {brandQuery.data?.brand.name ?? "your brand"}</h1>
          <p className={styles.sub}>
            Point it at your existing presence and it’ll learn your voice, audience, and
            what you talk about — then auto-fill the brand brain. It only fills gaps and
            adds to lists; it never overwrites what you’ve already set. You review and
            tweak afterward.
          </p>
        </header>

        {!running && job?.status !== "done" && (
          <div className={styles.form}>
            <Field label="Website & page URLs" hint="Your homepage, blog, about / story pages — we read each one (plus a few key pages on the first site). Add up to 6.">
              <div className={styles.urlList}>
                {urls.map((url, i) => (
                  <div key={i} className={styles.urlRow}>
                    <Input
                      value={url}
                      onChange={(e) => setUrlAt(i, e.target.value)}
                      placeholder="https://yourbrand.com"
                      inputMode="url"
                    />
                    {urls.length > 1 && (
                      <Btn
                        variant="ghost"
                        size="sm"
                        onClick={() => removeUrl(i)}
                        aria-label="Remove this URL"
                        iconLeft={<Icon name="x" size={14} />}
                      />
                    )}
                  </div>
                ))}
                {urls.length < MAX_URLS && (
                  <Btn variant="ghost" size="sm" onClick={addUrl} iconLeft={<Icon name="arrow-up-right" size={13} />}>
                    Add another URL
                  </Btn>
                )}
              </div>
            </Field>
            <Field label="Or paste anything in your voice (optional)" hint="Past captions, an about blurb, a newsletter — whatever sounds like you.">
              <Textarea
                value={pastedText}
                onChange={(e) => setPastedText(e.target.value)}
                placeholder="Paste copy that's already in your brand voice…"
                rows={5}
              />
            </Field>

            {createScan.error && <p className={styles.error}>{createScan.error.message}</p>}

            <Btn
              variant="primary"
              full
              size="lg"
              onClick={onScan}
              disabled={!canSubmit}
              loading={createScan.isPending}
              iconRight={<Icon name="zap" size={14} />}
            >
              Learn my brand
            </Btn>
          </div>
        )}

        {running && (
          <div className={styles.status}>
            <span className={styles.spinner} aria-hidden="true" />
            <span>
              {job?.status === "queued" ? "Queued…" : "Reading your brand + learning your voice…"} This
              can take up to a minute.
            </span>
          </div>
        )}

        {jobId && scanQuery.isError && (
          <div className={styles.failed}>
            <Icon name="info" size={16} color="var(--danger)" />
            <div>
              <strong>Lost track of that scan.</strong>
              <p>{scanQuery.error?.message ?? "Couldn't load the scan status."} Try again.</p>
            </div>
          </div>
        )}

        {job?.status === "failed" && (
          <div className={styles.failed}>
            <Icon name="info" size={16} color="var(--danger)" />
            <div>
              <strong>Couldn’t learn from those sources.</strong>
              <p>{job.error ?? "Nothing usable was found. Your brain was left unchanged."}</p>
              <Btn variant="secondary" size="sm" onClick={() => setJobId(undefined)}>
                Try different sources
              </Btn>
            </div>
          </div>
        )}

        {job?.status === "done" && (
          <div className={styles.done}>
            <span className={styles.doneMark} aria-hidden="true">
              <Icon name="check" size={28} strokeWidth={3} />
            </span>
            <h2 className={styles.doneTitle}>Learned and saved.</h2>
            <p className={styles.doneBody}>{job.summary ?? "Your brand brain has been updated."}</p>
            <div className={styles.doneActions}>
              <Btn variant="primary" href={`/w/${ws}/b/${brand}/edit`} iconRight={<Icon name="arrow-right" size={14} />}>
                Review the brain
              </Btn>
              <Btn variant="secondary" onClick={() => setJobId(undefined)}>
                Scan more sources
              </Btn>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
